// src/utils/woNumbers.js
// ===========================================================================
// Work order numbers: hand out only numbers nobody uses, and change a number
// everywhere at once.
//
// WHY NUMBERS DUPLICATED
//   Every proposal page takes the next number from a counter
//   (counters/PJMC_jobs, PDCS_jobs, ...; Scheduling uses
//   counters/scheduling_wo). The counter only knows the last number IT handed
//   out. It never checked whether that number was already on a record, so it
//   repeated one whenever it fell behind:
//     - a backup restore put an older counter back (the restore also restores
//       the "counters" collection), so the last few numbers came out again
//     - a WO typed or changed by hand to a number the counter had not reached
//       yet (renaming PJMC-10042 -> PJMC-10045 on Market Square, say)
//   The same number then sat on two properties and their totals, invoices and
//   payments mixed together.
//
// WHAT THIS DOES
//   getNextFreeWorkOrder(prefix)   next counter number that is not on ANY job,
//                                  invoice, proposal, payment or job total (any
//                                  upper/lower case). Used numbers are skipped
//                                  and the counter moves past them.
//   getNextFreeSchedulingNumber()  same for Scheduling's plain numbers.
//   isWOTaken(wo, { ignore })      is this WO on any record?
//   planWORenumber / applyWORenumber
//                                  change a WO on EVERY record that carries it
//                                  (job, its -2/-3 branches, invoices, payments,
//                                  proposal, checklist, Job Money, totals) -
//                                  optionally only at one property, for a number
//                                  two properties share. Logged in woRenumberLog
//                                  and undoable.
// ===========================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

export const RENUMBER_LOG = "woRenumberLog";

/* ------------------------------------------------------------------------ */
/* helpers                                                                  */
/* ------------------------------------------------------------------------ */

const clean = (wo) => String(wo ?? "").trim();
const upper = (wo) => clean(wo).toUpperCase();

/** As typed, UPPER, lower, Title - plus the plain number for numeric WOs. */
export const woSpellings = (wo) => {
  const v = clean(wo);
  if (!v) return [];
  const lower = v.toLowerCase();
  const out = new Set([v, v.toUpperCase(), lower, lower.charAt(0).toUpperCase() + lower.slice(1)]);
  const list = Array.from(out);
  if (/^\d+$/.test(v)) list.push(Number(v));
  return list;
};

export const normSiteName = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const whoAmI = () => {
  const u = auth.currentUser;
  return { uid: u?.uid || "", name: u?.displayName || u?.email || "" };
};

/* Where a WO can live, and the field it lives in. */
const WO_HOLDERS = [
  { collection: "jobs", field: "wo" },
  { collection: "addItem", field: "wo" },
  { collection: "approvedInvoices", field: "wo" },
  { collection: "proposals", field: "wo" },
  { collection: "deposits", field: "invoiceNumber" },
];

const inQuery = async (col, field, values) => {
  const out = [];
  for (let i = 0; i < values.length; i += 10) {
    const snap = await getDocs(query(collection(db, col), where(field, "in", values.slice(i, i + 10))));
    snap.docs.forEach((d) => out.push(d));
  }
  return out;
};

/* ------------------------------------------------------------------------ */
/* 1. Is a number taken?                                                    */
/* ------------------------------------------------------------------------ */

/**
 * True when any job, invoice, proposal, payment or job total uses this WO
 * (any upper/lower case).
 *   ignore: [{ collection, id }] records that may keep it (the one being saved)
 */
export const isWOTaken = async (wo, { ignore = [] } = {}) => {
  const spellings = woSpellings(wo);
  if (!spellings.length) return false;
  const skip = new Set(ignore.map((r) => `${r.collection}/${r.id}`));
  for (const h of WO_HOLDERS) {
    const docs = await inQuery(h.collection, h.field, spellings);
    if (docs.some((d) => !skip.has(`${h.collection}/${d.id}`))) return true;
  }
  for (const s of spellings.filter((x) => typeof x === "string")) {
    const snap = await getDoc(doc(db, "job_totals", s));
    if (snap.exists()) return true;
  }
  return false;
};

/* ------------------------------------------------------------------------ */
/* 2. Next free number                                                      */
/* ------------------------------------------------------------------------ */

const takeNextFromCounter = async (counterRef, start) =>
  runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const last = Number(snap.exists() ? snap.data()?.lastNumber : NaN);
    const next = (Number.isFinite(last) ? last : start - 1) + 1;
    if (snap.exists()) tx.update(counterRef, { lastNumber: next, updatedAt: serverTimestamp() });
    else tx.set(counterRef, { lastNumber: next, updatedAt: serverTimestamp() });
    return next;
  });

/**
 * "PJMC-10046": the next number on counters/{prefix}_jobs that no record
 * uses. Every number handed out moves the counter, so two people approving
 * at the same moment still get different numbers, and a used number is
 * skipped instead of handed out again.
 */
export const getNextFreeWorkOrder = async (prefix) => {
  const counterRef = doc(db, "counters", `${prefix}_jobs`);
  for (let tries = 0; tries < 200; tries += 1) {
    const n = await takeNextFromCounter(counterRef, 10001);
    const wo = `${prefix}-${n}`;
    // Read failures must not block an approval: treat as free, same as before.
    const taken = await isWOTaken(wo).catch((err) => {
      console.warn("WO check failed; using the counter number:", err);
      return false;
    });
    if (!taken) return wo;
    console.warn(`${wo} is already used by another record - skipped.`);
  }
  throw new Error(`Could not find a free ${prefix} work order number.`);
};

/** Scheduling's plain numbers (2001, 2002 ...), skipping any already used. */
export const getNextFreeSchedulingNumber = async () => {
  const counterRef = doc(db, "counters", "scheduling_wo");
  for (let tries = 0; tries < 200; tries += 1) {
    const n = await takeNextFromCounter(counterRef, 2001);
    const taken = await isWOTaken(String(n)).catch(() => false);
    if (!taken) return n;
    console.warn(`WO ${n} is already used by another record - skipped.`);
  }
  throw new Error("Could not find a free work order number.");
};

/** Prefix of a proposal-style WO ("PJMC-10045-2" -> "PJMC"), or "". */
export const proposalPrefixOf = (wo) => {
  const m = clean(wo).match(/^([A-Za-z]{3,6})-\d+/);
  return m ? m[1].toUpperCase() : "";
};

/* ------------------------------------------------------------------------ */
/* 3. Change a WO everywhere                                                */
/* ------------------------------------------------------------------------ */

/*
  Fields that hold a WO, per collection. "branch" fields follow the same
  rename for -2 / -3 branches (PJMC-10045-2 -> PJMC-10051-2).
*/
const RENUMBER_FIELDS = {
  jobs: ["wo", "continuedFromWO", "continuedToWO", "projectContinuedToWO", "contractBilling.depositWO"],
  addItem: ["wo"],
  approvedInvoices: ["wo", "invoiceNumber", "contractJobWO"],
  proposals: ["wo", "depositInvoiceWO", "contractJobWO"],
  deposits: ["invoiceNumber", "invoiceNumber2"],
  jobChecklist: ["wo"],
  bulk_material_expenses: ["wo"],
  // money and pay records that carry the WO as a label
  bulk_income: ["wo"],
  monthly_expenses: ["wo"],
  subcontractorPay: ["wo"],
  subcontractorPayments: ["wo"],
  bankStatementEntries: ["wo", "invoiceNumber"],
  recurring_job_occurrences: ["wo"],
  techCompletionRequests: ["wo"],
  transactions: ["wo"],
};
const SITE_FIELD = {
  jobs: ["siteName", "propertyName"],
  addItem: ["siteName", "propertyName"],
  approvedInvoices: ["siteName", "propertyName"],
  proposals: ["siteName"],
  deposits: ["property"],
  jobChecklist: ["siteName"],
  bulk_material_expenses: ["siteName", "propertyName"],
  bulk_income: ["siteName", "propertyName", "property"],
  monthly_expenses: ["siteName", "propertyName", "property"],
  subcontractorPay: ["siteName", "propertyName", "property"],
  subcontractorPayments: ["siteName", "propertyName", "property"],
  bankStatementEntries: ["siteName", "propertyName", "property"],
  recurring_job_occurrences: ["siteName"],
  techCompletionRequests: ["siteName"],
  transactions: ["siteName", "property"],
};

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

/**
 * oldWO -> newWO, and oldWO-N -> newWO-N when includeBranches.
 * Returns the new value, or null when `value` is not part of this WO chain.
 */
export const mapWO = (value, oldWO, newWO, includeBranches = true) => {
  const v = clean(value);
  if (!v) return null;
  const o = upper(oldWO);
  const V = v.toUpperCase();
  if (V === o) return clean(newWO);
  if (includeBranches && V.startsWith(`${o}-`)) {
    const rest = v.slice(o.length);
    if (/^-\d+(-\d+)*$/.test(rest)) return `${clean(newWO)}${rest}`;
  }
  return null;
};

/**
 * What a rename would change. Nothing is written.
 *   siteName: only records at this property (for a WO two properties share).
 *             Empty = every record with the WO.
 * Returns { changes: [{ collection, id, label, site, patch, before }], totals, conflicts }
 */
export const planWORenumber = async ({ oldWO, newWO, siteName = "", includeBranches = true, includeUnlinked = false }) => {
  const o = clean(oldWO);
  const n = clean(newWO);
  if (!o || !n) throw new Error("Enter the old and the new WO.");
  if (upper(o) === upper(n)) throw new Error("The new WO is the same as the old one.");

  const site = normSiteName(siteName);
  // Records with no property on them (a payment, a pay line) go with the
  // property whose job / invoice they point at. Anything that can't be tied
  // to this property is left alone and counted, never guessed.
  const siteJobIds = new Set();
  const siteInvoiceIds = new Set();
  const atSite = (col, data, id) => {
    if (!site) return true;
    const fields = SITE_FIELD[col] || [];
    const values = fields.map((f) => normSiteName(data[f])).filter(Boolean);
    if (values.length) return values.includes(site);
    const jobRef = data.jobId || data.originalJobId || data.sourceJobId;
    if (jobRef && siteJobIds.has(jobRef)) return true;
    if (data.approvedInvoiceId && siteInvoiceIds.has(data.approvedInvoiceId)) return true;
    if (col === "recurring_job_occurrences" && siteJobIds.has(String(id).replace(/_\d{4}-\d{2}-\d{2}$/, ""))) return true;
    return "unknown";
  };

  const byKey = new Map();
  const add = (col, d) => byKey.set(`${col}/${d.id}`, { col, d });

  const spellings = woSpellings(o);
  for (const [col, fields] of Object.entries(RENUMBER_FIELDS)) {
    for (const f of fields) {
      // exact
      const exact = await inQuery(col, f, spellings).catch(() => []);
      exact.forEach((d) => add(col, d));
      // branches (string range per spelling)
      if (includeBranches) {
        for (const s of spellings.filter((x) => typeof x === "string")) {
          const snap = await getDocs(
            query(collection(db, col), where(f, ">=", `${s}-`), where(f, "<=", `${s}-`))
          ).catch(() => ({ docs: [] }));
          snap.docs.forEach((d) => add(col, d));
        }
      }
    }
  }

  // First the records that name their property, so the rest can follow them.
  byKey.forEach(({ col, d }) => {
    const data = d.data() || {};
    const fields = SITE_FIELD[col] || [];
    if (!site || !fields.some((f) => normSiteName(data[f]) === site)) return;
    if (col === "jobs" || col === "addItem") siteJobIds.add(d.id);
    if (col === "approvedInvoices") siteInvoiceIds.add(d.id);
  });

  const changes = [];
  const unknownSite = [];
  byKey.forEach(({ col, d }) => {
    const data = d.data() || {};
    const where_ = atSite(col, data, d.id);
    if (where_ === "unknown" && !includeUnlinked) {
      unknownSite.push({ collection: col, id: d.id, label: `${data.wo || data.invoiceNumber || ""} ${data.description || data.memo || ""}`.trim().slice(0, 80) });
      return;
    }
    if (!where_) return;
    const patch = {};
    const before = {};
    (RENUMBER_FIELDS[col] || []).forEach((f) => {
      const cur = getPath(data, f);
      const next = mapWO(cur, o, n, includeBranches);
      if (next !== null && next !== cur) {
        patch[f] = next;
        before[f] = cur;
      }
    });
    if (!Object.keys(patch).length) return;
    const row = {
      collection: col,
      id: d.id,
      site: data.siteName || data.propertyName || data.property || "",
      label: `${data.wo || data.invoiceNumber || ""} ${data.scope || data.description || ""}`.trim().slice(0, 80),
      patch,
      before,
    };
    changes.push(row);
  });

  // Job Money ledgers (jobContracts): owner WO and linked WO lists.
  const ledgerChanges = [];
  const ledgerSnaps = [
    ...(await inQuery("jobContracts", "ownerWO", spellings.filter((x) => typeof x === "string")).catch(() => [])),
  ];
  for (const s of spellings.filter((x) => typeof x === "string")) {
    const snap = await getDocs(query(collection(db, "jobContracts"), where("linkedWOs", "array-contains", s))).catch(
      () => ({ docs: [] })
    );
    ledgerSnaps.push(...snap.docs);
    if (includeBranches) {
      const br = await getDocs(
        query(collection(db, "jobContracts"), where("ownerWO", ">=", `${s}-`), where("ownerWO", "<=", `${s}-\uf8ff`))
      ).catch(() => ({ docs: [] }));
      ledgerSnaps.push(...br.docs);
    }
  }
  const seenLedger = new Set();
  ledgerSnaps.forEach((d) => {
    if (seenLedger.has(d.id)) return;
    seenLedger.add(d.id);
    const data = d.data() || {};
    if (site && data.siteName && normSiteName(data.siteName) !== site) return;
    const patch = {};
    const before = {};
    const owner = mapWO(data.ownerWO, o, n, includeBranches);
    if (owner) {
      patch.ownerWO = owner;
      before.ownerWO = data.ownerWO;
    }
    if (Array.isArray(data.linkedWOs)) {
      const next = data.linkedWOs.map((w) => mapWO(w, o, n, includeBranches) || w);
      if (next.some((w, i) => w !== data.linkedWOs[i])) {
        patch.linkedWOs = next;
        before.linkedWOs = data.linkedWOs;
      }
    }
    if (Object.keys(patch).length) {
      ledgerChanges.push({ collection: "jobContracts", id: d.id, site: data.siteName || "", label: `Job Money ${data.ownerWO || ""}`, patch, before });
    }
  });

  // Job totals keyed by WO: every old key in the chain that exists.
  const woKeys = new Set();
  [...changes, ...ledgerChanges].forEach((c) => Object.entries(c.before).forEach(([f, v]) => {
    if (typeof v === "string" && f !== "linkedWOs") woKeys.add(v);
  }));
  woKeys.add(o);
  // Records at OTHER properties that keep the old WO (shared-number case),
  // and which WO values they still use.
  let othersKeep = 0;
  const keptElsewhere = new Set();
  const keptSites = new Set();
  if (site) {
    byKey.forEach(({ col, d }) => {
      const data = d.data() || {};
      const w = atSite(col, data, d.id);
      if (w === true || w === "unknown") return;
      othersKeep += 1;
      keptSites.add(data.siteName || data.propertyName || data.property || "");
      (RENUMBER_FIELDS[col] || []).forEach((f) => {
        const v = getPath(data, f);
        if (typeof v === "string" || typeof v === "number") keptElsewhere.add(upper(v));
      });
    });
  }

  // A total whose WO is still used at another property stays there: it is
  // not moved and NOT copied, so the same money is never counted twice.
  const totals = [];
  const totalsKept = [];
  for (const k of woKeys) {
    for (const col of ["job_totals", "job_payment_totals"]) {
      const snap = await getDoc(doc(db, col, k)).catch(() => null);
      if (!snap?.exists()) continue;
      const row = { collection: col, from: k, to: mapWO(k, o, n, includeBranches) || k, data: snap.data() };
      if (keptElsewhere.has(upper(k))) totalsKept.push(row);
      else totals.push(row);
    }
  }

  const newTaken = await isWOTaken(n).catch(() => false);

  return {
    oldWO: o,
    newWO: n,
    siteName,
    includeBranches,
    includeUnlinked,
    changes: [...changes, ...ledgerChanges],
    unknownSite,
    totals,
    totalsKept,
    othersKeep,
    keptSites: Array.from(keptSites).filter(Boolean),
    newTaken,
  };
};

/**
 * Apply a plan from planWORenumber. Totals move to the new key; a total on a
 * WO another property still uses stays where it is (plan.totalsKept).
 * Everything is logged in woRenumberLog for undo.
 */
export const applyWORenumber = async (plan) => {
  if (!plan?.changes?.length && !plan?.totals?.length) return { changed: 0 };
  const logRef = doc(collection(db, RENUMBER_LOG));
  const totalWrites = [];

  // Writes in chunks (a batch holds 500).
  const ops = [];
  plan.changes.forEach((c) => ops.push((b) => b.update(doc(db, c.collection, c.id), { ...c.patch, woRenumberedAt: new Date().toISOString() })));
  for (const t of plan.totals) {
    const toRef = doc(db, t.collection, t.to);
    const existing = await getDoc(toRef);
    totalWrites.push({ ...t, existingAtNew: existing.exists() ? existing.data() : null });
    if (existing.exists()) continue; // never overwrite a total already on the new WO
    const payload = { ...t.data, movedFrom: t.from, updatedAt: serverTimestamp() };
    if (t.collection === "job_totals") Object.assign(payload, { jobId: t.to, wo: t.to });
    if (t.collection === "job_payment_totals") Object.assign(payload, { wo: t.to });
    ops.push((b) => b.set(toRef, payload));
    ops.push((b) => b.delete(doc(db, t.collection, t.from)));
  }

  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    ops.slice(i, i + 400).forEach((op) => op(batch));
    await batch.commit();
  }

  // Financials rows mirror each payment, with the WO written into the memo
  // ("Check# 1234 | WO PJMC-10045 | ..."). Rewrite that text too.
  const memoChanges = [];
  for (const c of plan.changes.filter((x) => x.collection === "deposits" && x.patch.invoiceNumber)) {
    try {
      const snap = await getDocs(query(collection(db, "transactions"), where("sourceId", "==", c.id)));
      for (const t of snap.docs) {
        const memo = String(t.data()?.memo || "");
        const from = String(c.before.invoiceNumber);
        if (!memo.includes(from)) continue;
        const next = memo.split(`WO ${from}`).join(`WO ${c.patch.invoiceNumber}`);
        if (next === memo) continue;
        memoChanges.push({ collection: "transactions", id: t.id, before: { memo }, patch: { memo: next }, site: c.site, label: "Financials memo" });
      }
    } catch (err) {
      console.warn("Could not update the Financials memo for payment", c.id, err);
    }
  }
  for (let i = 0; i < memoChanges.length; i += 400) {
    const batch = writeBatch(db);
    memoChanges.slice(i, i + 400).forEach((m) => batch.update(doc(db, m.collection, m.id), m.patch));
    await batch.commit();
  }

  const by = whoAmI();
  await setDoc(logRef, {
    oldWO: plan.oldWO,
    newWO: plan.newWO,
    siteName: plan.siteName || "",
    includeBranches: plan.includeBranches,
    othersKeep: plan.othersKeep || 0,
    changes: [...plan.changes, ...memoChanges].map(({ collection: c, id, before, patch, site, label }) => ({ collection: c, id, before, patch, site, label })),
    totals: totalWrites.map(({ collection: c, from, to, data, existingAtNew }) => ({
      collection: c,
      from,
      to,
      data,
      skipped: !!existingAtNew,
      deletedOld: !existingAtNew,
    })),
    by: by.name,
    byUid: by.uid,
    at: serverTimestamp(),
    undone: false,
  });

  return { changed: plan.changes.length, totals: totalWrites.length, logId: logRef.id };
};

/** Put a renumber back exactly as it was. */
export const undoWORenumber = async (logId) => {
  const ref = doc(db, RENUMBER_LOG, logId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("That change was not found.");
  const log = snap.data();
  if (log.undone) throw new Error("Already undone.");
  const ops = [];
  (log.changes || []).forEach((c) => ops.push((b) => b.update(doc(db, c.collection, c.id), c.before)));
  (log.totals || []).forEach((t) => {
    if (t.skipped) return;
    ops.push((b) => b.set(doc(db, t.collection, t.from), t.data));
    ops.push((b) => b.delete(doc(db, t.collection, t.to)));
  });
  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    ops.slice(i, i + 400).forEach((op) => op(batch));
    await batch.commit();
  }
  await setDoc(ref, { undone: true, undoneAt: serverTimestamp(), undoneBy: whoAmI().name }, { merge: true });
};
